import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Navigate } from 'react-router-dom';
import ErrorCodes from '../consts/ErrorCodes';
import Urls from '../consts/Urls';
import useStore from '../store/store';

const api = axios.create({
    baseURL: Urls.baseHttpUrl,
});

const { setFormError, setLoading } = useStore.getState();

api.interceptors.response.use(
    async (response) => {
        const {
            data: { success, errCode },
        } = response;
        if (!success && errCode === ErrorCodes.invalid_token) {
            console.error('Received invalid_token error!');

            if (response.config.url === '/security/refresh-access-token') {
                console.error('Failed to refresh access token.');
                setLoading(false);
                return Promise.reject(
                    new Error('Failed to refresh access token.')
                );
            }

            const refreshToken = await localStorage.getItem('refreshToken');

            if (refreshToken) {
                const decodedRefreshToken = jwtDecode(refreshToken);

                // If refresh token is not expired then refresh main token.
                const secondsSinceEpoch = Math.floor(Date.now() / 1000);
                if (decodedRefreshToken.exp < secondsSinceEpoch) {
                    cleanUp();

                    return Promise.reject(
                        new Error('Refresh token is expired')
                    );
                }

                // Save config in order to run the failed request after successfully refreshing token.
                const failedRequest = response.config;
                api.defaults.headers.common.Authorization = `Bearer ${refreshToken}`;

                console.info('Fetching new token...');
                return api
                    .post('/security/refresh-access-token')
                    .then((refreshTokenResponse) => {
                        const { success, accessToken, errCode } =
                            refreshTokenResponse.data;
                        if (success && accessToken && !errCode) {
                            api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
                            localStorage.setItem('accessToken', accessToken);
                            failedRequest.headers.Authorization = `Bearer ${accessToken}`;
                            return api(failedRequest);
                        } else {
                            return Promise.reject(
                                new Error('Failed to refresh access token.')
                            );
                        }
                    })
                    .catch((error) => {
                        cleanUp();
                        return Promise.reject(error);
                    });
            } else {
                cleanUp();
                return Promise.reject(
                    new Error('No refresh token or application id available')
                );
            }
        } else if (
            !success &&
            (errCode === ErrorCodes.auth_failed ||
                errCode === ErrorCodes.access_denied)
        ) {
            console.error(`Received ${errCode}`);
            if (response.config.url !== '/security/confirm') cleanUp();
            setFormError(errCode);
            setLoading(false);
            return Promise.reject(new Error(errCode));
        } else {
            return Promise.resolve(response);
        }
    },
    (error) => {
        return Promise.reject(error);
    }
);

const cleanUp = () => {
    // Remove the headers.
    api.defaults.headers.common.Authorization = null;

    // Clean storage.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('phone');

    setLoading(false);

    // Go to login page.
    return <Navigate to="/login" />;
};

export default api;
